import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { BrandButton as Button, HospiceSearchableSelect } from "@/components";
import { useAuthenticatedApi } from "@/utils/callApi";

/**
 * Reusable publish survey dialog component
 * Used by SurveyBuilder, SurveyDuplicator, and SurveyPreview
 * 
 * @component
 * @param {Object} props - Component props
 * @param {boolean} props.show - Controls dialog visibility
 * @param {Function} props.onClose - Callback when dialog is closed
 * @param {string} props.surveyTitle - Title of the survey being published
 * @param {Function} props.onPublish - Callback when publish button is clicked
 * @param {Array} props.hospices - Array of hospice objects with {id, name}
 * @param {Array} props.selectedHospices - Array of selected hospice IDs
 * @param {Function} props.setSelectedHospices - Setter for selected hospices
 * @param {boolean} props.publishToAll - Whether to publish to all hospices
 * @param {Function} props.setPublishToAll - Setter for publishToAll flag
 * @param {string} props.startDate - Start date in ISO format (YYYY-MM-DD)
 * @param {Function} props.setStartDate - Setter for start date
 * @param {string} props.endDate - End date in ISO format (YYYY-MM-DD)
 * @param {Function} props.setEndDate - Setter for end date
 * @param {boolean} props.autoClose - Whether to auto-close on end date
 * @param {Function} props.setAutoClose - Setter for autoClose flag
 */
export default function PublishSurveyDialog({
  // Display control
  show,
  onClose,
  
  // Survey data
  surveyTitle,
  surveyRoles,
  
  // Publish function
  onPublish,
  
  // Hospice selections
  hospices,
  selectedHospices,
  setSelectedHospices,
  publishToAll,
  setPublishToAll,
  
  // Date selection
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  
  // Auto close
  autoClose,
  setAutoClose
}) {
  // Hospice lists from api (created in Manage Hospice Lists page)
  const [selectedListId, setSelectedListId] = useState("");
  const [selectedListIds, setSelectedListIds] = useState([]);
  const [hospiceLists, setLists] = useState([]);

  const { secureHelpers } = useAuthenticatedApi();

  const normalizeHospiceList = (list) => ({
    id: list.id ?? list.Id,
    name: list.name ?? list.Name,
    hospiceIds: list.hospiceIds ?? list.targets ?? list.Targets ?? [],
  });

  useEffect(() => {
    if (!show) return;

    const fetchLists = async () => {
      try {
        const data = await secureHelpers.get(`/api/Hospices/Lists`);
        const lists = Array.isArray(data) ? data.map(normalizeHospiceList) : [];
        setLists(lists);
      } catch (error) {
        console.error('Failed to load hospice lists:', error);
        setLists([]);
      }
    };

    fetchLists();
    // secureHelpers is intentionally omitted because useAuthenticatedApi creates
    // a fresh helper object each render, which can cause repeated fetches here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  const handleListSelect = (listId) => {
    setSelectedListId(listId);
    if (!listId) return;
    const list = hospiceLists.find(l => String(l.id) === String(listId));
    if (!list) return;
    const normalizedListId = String(list.id);
    setSelectedListIds(prev => prev.includes(normalizedListId) ? prev : [...prev, normalizedListId]);
    // Merge list hospice IDs with already-selected ones (deduped)
    const merged = [...new Set([...(selectedHospices || []), ...list.hospiceIds])];
    setSelectedHospices(merged);
  };

  // Set default dates when dialog opens (only if not already set)
  useEffect(() => {
    if (!show) {
      setSelectedListId("");
      setSelectedListIds([]);
    }
    if (show && !startDate && !endDate) {
      const today = new Date().toISOString().split('T')[0];
      const nextQuarter = new Date();
      nextQuarter.setMonth(nextQuarter.getMonth() + 3);
      const endDateDefault = nextQuarter.toISOString().split('T')[0];
      
      setStartDate(today);
      setEndDate(endDateDefault);
    }
  }, [show, startDate, endDate, setStartDate, setEndDate]);

  // Prevent background page scrolling while modal is open
  useEffect(() => {
    if (show) {
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [show]);
  
  if (!show) return null;
  
  return ReactDOM.createPortal(
    <>
      <div className="modal-backdrop show" style={{ zIndex: 10000 }}></div>
      <div className="modal show d-block" tabIndex="-1" style={{ zIndex: 10001, position: 'fixed', top: 0, left: 0, width: '100%', height: '100%' }}>
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content">
            <div className="modal-header" style={{ backgroundColor: "var(--color-primary)", color: "#fff" }}>
              <h5 className="modal-title">Publish Survey: {surveyTitle}</h5>
            </div>
            <div className="modal-body">
              <p>Publishing will create survey instances for the selected hospices. Members will be able to see and complete these surveys in the Member Data Portal.</p>
              <p style={{ fontSize:  "var(--font-heading-sm)" }}>Note: Choose automatic closure on the end date, or close manually later from Survey Monitoring for flexibility with late submissions.</p>
              
              {/* Warning if no roles selected */}
              {(Array.isArray(surveyRoles) && surveyRoles.length === 0) && (
                <div className="alert alert-warning d-flex align-items-start" role="alert">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="currentColor" className="bi bi-exclamation-triangle-fill flex-shrink-0 me-2" viewBox="0 0 16 16" style={{ marginTop: '2px' }}>
                    <path d="M8.982 1.566a1.13 1.13 0 0 0-1.96 0L.165 13.233c-.457.778.091 1.767.98 1.767h13.713c.889 0 1.438-.99.98-1.767L8.982 1.566zM8 5c.535 0 .954.462.9.995l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 5.995A.905.905 0 0 1 8 5zm.002 6a1 1 0 1 1 0 2 1 1 0 0 1 0-2z"/>
                  </svg>
                  <div>
                    <span style={{ fontFamily: "'Neutraface-Demi', Arial, sans-serif" }}>No roles assigned!</span>
                    <p className="mb-0 mt-1" style={{ fontSize: 'var(--font-sm)' }}>This survey has no roles assigned. If you publish it, <span style={{ fontFamily: "'Neutraface-Demi', Arial, sans-serif" }}>no members will be able to see it</span> because role-based filtering requires at least one role. Please go back and assign roles (e.g., CEO, Finance Director) before publishing.</p>
                  </div>
                </div>
              )}
              
              {/* Audience Selection */}
              <div className="mb-3">
                <label className="form-label ">Select Audience:</label>
                <div className="form-check">
                  <input
                    type="radio"
                    className="form-check-input"
                    id="publishToAll"
                    checked={publishToAll}
                    onChange={() => setPublishToAll(true)}
                  />
                  <label className="form-check-label" htmlFor="publishToAll">
                    All Hospices
                  </label>
                </div>
                <div className="form-check">
                  <input
                    type="radio"
                    className="form-check-input"
                    id="publishToList"
                    checked={!publishToAll}
                    onChange={() => setPublishToAll(false)}
                  />
                  <label className="form-check-label" htmlFor="publishToList">
                    Select Specific Hospices
                  </label>
                </div>
              </div>

              {/* Hospice Selection (only if not publishing to all) */}
              {!publishToAll && (
                <div className="mb-4">
                  {/* Hospice List quick-apply */}
                  {hospiceLists.length > 0 && (
                    <div className="card mb-3 p-3" style={{ backgroundColor: "#f8f9fa" }}>
                      <p className="text-heading-sm semibold-text mb-2">Apply from Hospice List:</p>
                      <select
                        className="form-select"
                        value={selectedListId}
                        onChange={e => handleListSelect(e.target.value)}
                      >
                        <option value="">-- Select a list to apply --</option>
                        {hospiceLists.map(list => (
                          <option key={list.id} value={list.id}>
                            {list.name} ({list.hospiceIds.length} hospice{list.hospiceIds.length !== 1 ? 's' : ''})
                          </option>
                        ))}
                      </select>
                      <div className="mt-4">
                        <p className="mb-2">You have selected the following lists:</p>
                        {selectedListIds.length > 0 ? (
                          <ul style={{ fontSize: 'var(--font-lg)', paddingLeft: '1.5rem' }}>
                            {hospiceLists.filter(list => selectedListIds.includes(String(list.id))).map(list => (
                              <li key={list.id}>{list.name} ({list.hospiceIds.length} hospice{list.hospiceIds.length !== 1 ? 's' : ''})</li>
                            ))}
                          </ul>
                        ) : (
                          <p className="text-muted mb-0">No lists selected yet.</p>
                        )}
                      </div>
                      <small className="text-muted mt-1 d-block">
                        Selecting a list will add its hospices to your selection below. You can then add or remove individual hospices. Please note if some of the hospices in the list are already selected, they will not be duplicated.
                      </small>
                    </div>
                  )}
                  <div className="mb-2 card p-3" style={{ backgroundColor: "#f8f9fa" }}>
                    <p className="text-heading-sm semibold-text mb-2">Select Individual Hospices:</p>
                    <HospiceSearchableSelect
                      hospices={hospices}
                      value={selectedHospices}
                      onChange={setSelectedHospices}
                      placeholder="Search and select hospices..."
                      multiple={true}
                      maxHeight={300}
                    />
                  </div>
                  <small className="text-muted mt-1 d-block">
                    {(selectedHospices || []).length} hospice(s) selected
                  </small>

                </div>
              )}

              {/* Date Selection */}
              <div className="row mb-3">
                <div className="col-md-6">
                  <label className="form-label ">Start Date:</label>
                  <input
                    type="date"
                    className="form-control"
                    value={startDate}
                    readOnly
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label ">End Date: <span className="text-danger">*</span></label>
                  <input
                    type="date"
                    className="form-control"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    required
                  />
                </div>
              </div>

              {/* Auto Close Option */}
              <div className="mb-3">
                <div className="form-check">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="autoClose"
                    checked={autoClose}
                    onChange={(e) => setAutoClose(e.target.checked)}
                  />
                  <label className="form-check-label" htmlFor="autoClose">
                    Automatically close survey on end date 
                  </label>
                </div>
              </div>
            </div>
            
            {/* Modal Footer - Always visible */}
            <div className="modal-footer">
              <Button variant="success" onClick={onPublish}>
                Publish Now
              </Button>
              <Button
                variant="secondary"
                onClick={onClose}
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      </div>
    </>,
    document.body
  );
}

// PropTypes for type checking and documentation
PublishSurveyDialog.propTypes = {
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  surveyTitle: PropTypes.string.isRequired,
  onPublish: PropTypes.func.isRequired,
  hospices: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    name: PropTypes.string.isRequired
  })).isRequired,
  selectedHospices: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  ).isRequired,
  setSelectedHospices: PropTypes.func.isRequired,
  publishToAll: PropTypes.bool.isRequired,
  setPublishToAll: PropTypes.func.isRequired,
  startDate: PropTypes.string.isRequired,
  setStartDate: PropTypes.func.isRequired,
  endDate: PropTypes.string.isRequired,
  setEndDate: PropTypes.func.isRequired,
  autoClose: PropTypes.bool.isRequired,
  setAutoClose: PropTypes.func.isRequired
};
